import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { PageHeader } from "../components/PageHeader";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { ArchiveRestore, Trash2, Search, Lock } from "lucide-react";
import { useAuth } from "../lib/auth";
import { statusMeta, formatDate } from "../lib/format";
import DeleteProjectDialog from "../components/DeleteProjectDialog";

export default function ArchivedProjectsPage() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const { canDelete } = useAuth();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/projects", { params: { archived: true } });
      setProjects(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = projects.filter((p) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return (
      p.name.toLowerCase().includes(q) ||
      (p.client || "").toLowerCase().includes(q) ||
      (p.location || "").toLowerCase().includes(q)
    );
  });

  const doRestore = async (p) => {
    await api.post(`/projects/${p.id}/restore`);
    toast.success(`Restored "${p.name}"`);
    load();
  };

  const doDelete = async (p) => {
    try {
      await api.delete(`/projects/${p.id}`);
      toast.success(`Permanently deleted "${p.name}"`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
      throw e;
    }
  };

  return (
    <div>
      <PageHeader
        eyebrow="History"
        title="Archived Projects"
        description="Archived projects stay searchable and retain all inventory history. Restore them anytime — or delete permanently."
        testId="archived-header"
      />

      <div className="p-4 md:p-8">
        <div className="mb-4 flex items-center justify-between gap-4">
          <div className="relative w-full max-w-sm">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search archived projects…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="archived-search-input"
              className="pl-9 rounded-sm border-slate-300"
            />
          </div>
          <div className="text-xs font-mono text-slate-500">{filtered.length} archived</div>
        </div>

        <div className="border border-slate-200 rounded-md overflow-x-auto bg-white shadow-sm">
          <table className="w-full text-sm min-w-[720px]" data-testid="archived-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-xs font-bold uppercase tracking-wider text-slate-500">
                <th className="p-4">Project</th>
                <th className="p-4">Client</th>
                <th className="p-4">Location</th>
                <th className="p-4">Status</th>
                <th className="p-4">Archived</th>
                <th className="p-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="p-4 md:p-8 text-center text-slate-500 font-mono text-sm">Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={6} className="p-12 text-center">
                    <div className="text-slate-500 font-mono text-sm">No archived projects.</div>
                  </td>
                </tr>
              ) : (
                filtered.map((p) => {
                  const meta = statusMeta(p.status, true);
                  return (
                    <tr key={p.id} className="border-b border-slate-200 last:border-b-0 hover:bg-slate-50 transition-colors" data-testid={`archived-row-${p.id}`}>
                      <td className="p-4 font-semibold text-slate-900">{p.name}</td>
                      <td className="p-4 text-slate-700">{p.client || "—"}</td>
                      <td className="p-4 text-slate-700">{p.location || "—"}</td>
                      <td className="p-4">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-sm text-xs font-semibold ${meta.cls}`}>
                          {meta.label}
                        </span>
                      </td>
                      <td className="p-4 text-slate-500 font-mono text-xs">{formatDate(p.updated_at)}</td>
                      <td className="p-4">
                        <div className="flex items-center gap-2 justify-end">
                          <Button
                            onClick={() => doRestore(p)}
                            variant="outline"
                            size="sm"
                            className="rounded-sm border-slate-300"
                            data-testid={`archived-restore-${p.id}`}
                          >
                            <ArchiveRestore size={14} className="mr-1.5" /> Restore
                          </Button>
                          {canDelete ? (
                            <Button
                              onClick={() => setDeleteTarget(p)}
                              size="sm"
                              className="bg-red-600 hover:bg-red-700 text-white rounded-sm"
                              data-testid={`archived-delete-${p.id}`}
                            >
                              <Trash2 size={14} className="mr-1.5" /> Delete
                            </Button>
                          ) : (
                            <Button
                              disabled
                              size="sm"
                              variant="outline"
                              className="rounded-sm"
                              data-testid={`archived-delete-disabled-${p.id}`}
                            >
                              <Lock size={14} className="mr-1.5" /> Admin only
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <DeleteProjectDialog
        open={!!deleteTarget}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
        project={deleteTarget}
        onArchive={async () => { await api.post(`/projects/${deleteTarget.id}/restore`); toast.success("Restored"); load(); }}
        onDelete={() => doDelete(deleteTarget)}
      />
    </div>
  );
}
